import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Shield, LogOut, Mail, Phone, Trash2, CheckCircle2, Circle, Inbox, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const token = localStorage.getItem("rpc_admin_token");
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const logout = useCallback(() => {
    localStorage.removeItem("rpc_admin_token");
    navigate("/admin/login");
  }, [navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/contacts`, authHeader);
      setContacts(data);
    } catch (err) {
      if (err?.response?.status === 401) logout();
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logout]);

  useEffect(() => {
    if (!token) { navigate("/admin/login"); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleRead = async (id) => {
    try { await axios.patch(`${API}/admin/contacts/${id}/read`, {}, authHeader); load(); } catch (e) {}
  };
  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta solicitud?")) return;
    try { await axios.delete(`${API}/admin/contacts/${id}`, authHeader); load(); } catch (e) {}
  };

  const unread = contacts.filter((c) => !c.read).length;
  const fmt = (d) => new Date(d).toLocaleString("es-CL", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

  return (
    <div className="min-h-screen bg-[#f4f6f9]">
      <header className="bg-[#0d2140] px-5 md:px-8 py-4 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-2">
          <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-white/10"><Shield className="w-5 h-5 text-[#f5871f]" /></span>
          <span className="font-display font-700 text-white text-lg">GRUPO <span className="text-[#f5871f]">RPC</span> · Admin</span>
        </div>
        <button onClick={logout} className="flex items-center gap-2 text-white/80 hover:text-white text-sm font-600"><LogOut className="w-4 h-4" />Salir</button>
      </header>

      <main className="max-w-5xl mx-auto px-5 md:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-display font-700 text-2xl uppercase text-[#0d2140]">Solicitudes de contacto</h1>
            <p className="text-[#0d2140]/50 text-sm">{contacts.length} en total · {unread} sin leer</p>
          </div>
          <button onClick={load} className="flex items-center gap-2 rounded-xl border border-[#0d2140]/15 px-4 py-2 text-sm font-600 text-[#0d2140] hover:border-[#f5871f]"><RefreshCw className="w-4 h-4" />Actualizar</button>
        </div>

        {loading ? (
          <p className="text-center text-[#0d2140]/50 py-20">Cargando...</p>
        ) : contacts.length === 0 ? (
          <div className="text-center py-20 text-[#0d2140]/40"><Inbox className="w-12 h-12 mx-auto mb-3" /><p>Aún no hay solicitudes.</p></div>
        ) : (
          <div className="space-y-4">
            {contacts.map((c) => (
              <div key={c.id} className={`rounded-2xl border p-5 bg-white ${c.read ? "border-[#0d2140]/10" : "border-[#f5871f]/50 shadow-sm"}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      {!c.read && <span className="w-2 h-2 rounded-full bg-[#f5871f]" />}
                      <h3 className="font-display font-700 text-lg text-[#0d2140] uppercase">{c.name}</h3>
                    </div>
                    <div className="flex flex-wrap gap-4 mt-2 text-sm">
                      <a href={`mailto:${c.email}`} className="flex items-center gap-1.5 text-[#0d2140]/70 hover:text-[#f5871f]"><Mail className="w-4 h-4" />{c.email}</a>
                      {c.phone && <a href={`tel:${c.phone}`} className="flex items-center gap-1.5 text-[#0d2140]/70 hover:text-[#f5871f]"><Phone className="w-4 h-4" />{c.phone}</a>}
                    </div>
                    {c.message && <p className="mt-3 text-[#0d2140]/80 text-sm bg-[#f4f6f9] rounded-lg p-3">{c.message}</p>}
                    <p className="mt-2 text-xs text-[#0d2140]/40">{fmt(c.created_at)}</p>
                  </div>
                  <div className="flex flex-col gap-2">
                    <button onClick={() => toggleRead(c.id)} title={c.read ? "Marcar no leído" : "Marcar leído"} className="text-[#0d2140]/50 hover:text-green-600">
                      {c.read ? <CheckCircle2 className="w-5 h-5 text-green-600" /> : <Circle className="w-5 h-5" />}
                    </button>
                    <button onClick={() => remove(c.id)} title="Eliminar" className="text-[#0d2140]/50 hover:text-red-600"><Trash2 className="w-5 h-5" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
